// assets/sticker-upload-redirect.js
import { saveArtwork } from '@theme/sticker-artwork-store';

// A generous ceiling for a source photo, well above what any legitimate design upload needs, but
// low enough to catch the most common mobile failure mode before it ever reaches IndexedDB: a
// full-resolution camera photo (easily 15-25MB+ on a modern phone) that then exceeds the
// browser's storage quota — particularly tight/unpredictable in Safari private browsing.
const MAX_ARTWORK_BYTES = 25 * 1024 * 1024;

/**
 * The option values a shopper currently has selected, as Shopify's own option-value ids.
 *
 * One per option, in the order the picker renders them. Each option renders twice on this page (a
 * desktop copy and a mobile one — see design-variant-option.liquid's dual render), and both share a
 * radio group, so they always agree; the first of each option name is taken and the duplicate
 * skipped.
 *
 * Handles both radios and <select>-based option styles, since the same block can render either
 * depending on its Style setting.
 *
 * @returns {string[]}
 */
function readSelectedOptionValueIds() {
  /** @type {Set<string>} */
  const seenOptions = new Set();
  /** @type {string[]} */
  const ids = [];

  for (const element of document.querySelectorAll('variant-picker [data-option-value-id]')) {
    const isCheckedInput = element instanceof HTMLInputElement && element.checked;
    const isSelectedOption = element instanceof HTMLOptionElement && element.selected;
    if (!isCheckedInput && !isSelectedOption) continue;

    const optionName = /** @type {HTMLElement} */ (element).dataset.optionName ?? '';
    if (seenOptions.has(optionName)) continue;

    const id = /** @type {HTMLElement} */ (element).dataset.optionValueId;
    if (!id) continue;

    seenOptions.add(optionName);
    ids.push(id);
  }

  return ids;
}

/**
 * Last-resort variant lookup, used only when a product renders no option inputs at all for
 * readSelectedOptionValueIds to read. Mirrors the plain flow's copy — duplicated rather than
 * imported, matching how these two redirect scripts are kept separate everywhere else so a change
 * made for one flow can't quietly alter the other.
 *
 * @returns {string | undefined}
 */
function readSelectedVariantId() {
  /** @type {Map<string, { hasChecked: boolean, firstId: string | undefined }>} */
  const groups = new Map();

  for (const input of document.querySelectorAll('variant-picker input[data-option-name]')) {
    if (!(input instanceof HTMLInputElement)) continue;
    if (input.checked && input.dataset.variantId) return input.dataset.variantId;

    const name = input.dataset.optionName ?? '';
    const group = groups.get(name) ?? { hasChecked: false, firstId: undefined };
    if (input.checked) group.hasChecked = true;
    if (!group.firstId && input.dataset.variantId) group.firstId = input.dataset.variantId;
    groups.set(name, group);
  }

  // Unresolved combination — fall back to the option group that has nothing checked, whose ids are
  // rendered against everything that IS selected. Groups that already have a checked sibling carry
  // ids for switching AWAY from the current choice, so following one would undo it. See the plain
  // flow's copy for the full reasoning and the trade this makes.
  for (const group of groups.values()) {
    if (!group.hasChecked && group.firstId) return group.firstId;
  }

  const variantJson = document.querySelector('variant-picker script[type="application/json"]')?.textContent;
  if (variantJson) {
    try {
      const id = JSON.parse(variantJson)?.id;
      if (id) return String(id);
    } catch {
      // fall through to the URL below
    }
  }

  return new URL(window.location.href).searchParams.get('variant') || undefined;
}

/**
 * Wraps the button on the plain product page — a generic "wire up this button, then navigate"
 * helper shared by two otherwise-separate block files: blocks/file-upload.liquid's real file input
 * (the plain sticker flow) and blocks/sheet-file-upload.liquid's plain button (the sticker-sheet
 * flow). Branches purely on which markup happens to be inside it, not on which flow it's in — it
 * has no sticker/sheet-specific logic of its own, which is why it stays a single shared file
 * rather than being forked alongside the two blocks it serves.
 *
 * With a real file input: picking a file doesn't process it in place — it persists the raw file to
 * IndexedDB (a real browser navigation is about to discard all JS state) and redirects to the
 * dedicated design page (templates/product.sticker-design.json), which reads it back on load. See
 * assets/sticker-artwork-store.js for why IndexedDB instead of session/local storage. With no file
 * input at all, there's nothing to persist — the sheet design page starts from a blank canvas
 * instead (see blank_canvas_mode on sections/sticker-sheet-design-editor.liquid), so this just
 * redirects.
 */
class StickerUploadRedirect extends HTMLElement {
  connectedCallback() {
    const input = this.querySelector('input[type="file"]');
    if (input instanceof HTMLInputElement) {
      input.addEventListener('change', this.#handleChange);
      return;
    }

    const button = this.querySelector('button[data-direct-navigate]');
    if (button instanceof HTMLButtonElement) button.addEventListener('click', this.#handleDirectNavigate);
  }

  disconnectedCallback() {
    const input = this.querySelector('input[type="file"]');
    if (input instanceof HTMLInputElement) input.removeEventListener('change', this.#handleChange);

    const button = this.querySelector('button[data-direct-navigate]');
    if (button instanceof HTMLButtonElement) button.removeEventListener('click', this.#handleDirectNavigate);
  }

  /** @param {Event} event */
  #handleChange = async (event) => {
    const input = /** @type {HTMLInputElement} */ (event.target);
    const file = input.files?.[0];
    if (!file) return;

    if (file.size > MAX_ARTWORK_BYTES) {
      input.value = '';
      window.alert('That image is too large. Please choose a file under 25MB.');
      return;
    }

    // saveArtwork can genuinely reject — most commonly IndexedDB storage quota exceeded (easy to
    // hit with a full-resolution mobile camera photo, especially in Safari private browsing,
    // which caps IndexedDB much more aggressively than normal browsing). Uncaught, that silently
    // aborts this whole async handler before the redirect below ever runs — from the customer's
    // side, tapping the upload button just does nothing, with no error and no way to tell why.
    try {
      await saveArtwork(file);
    } catch (error) {
      console.error('Failed to save the uploaded artwork:', error);
      input.value = '';
      window.alert(
        "Sorry, we couldn't process that image. If you're browsing in private/incognito mode, " +
          'please try again in a normal browser window — some browsers block the storage this page ' +
          'needs while in that mode. Otherwise, please try again or use a smaller file.'
      );
      return;
    }

    await this.#redirectToDesignPage();
  };

  /** No file to persist — the design page (blank_canvas_mode) starts from a blank canvas. */
  #handleDirectNavigate = async () => {
    await this.#redirectToDesignPage();
  };

  async #redirectToDesignPage() {
    const designUrl = new URL(this.dataset.designUrl ?? '', window.location.origin);

    // option_values, not a variant id — see the plain flow's sticker-upload-redirect.js for why a
    // variant id proved unreliable to carry from this page. A checked option's own value id is what
    // the shopper picked, correct immediately, and valid even for a combination that resolves to no
    // variant; the design page reselects from it server side.
    const optionValueIds = readSelectedOptionValueIds();
    if (optionValueIds.length) {
      designUrl.searchParams.set('option_values', optionValueIds.join(','));
    } else {
      const variantId = readSelectedVariantId();
      if (variantId) designUrl.searchParams.set('variant', String(variantId));
    }

    // Vinyl/Size/Quantity are real variants, already carried over via ?variant= above — but
    // Shape/Finish (and any other custom-option field) are line-item properties, not encoded in
    // the variant id, so they need to travel separately. The design page reads these same query
    // params back on load (see sections/product-design-editor.liquid) to pre-select the matching
    // radio/select for each one instead of resetting to its default. Read straight off the inputs
    // (not via the cart form's FormData) since custom-option fields only reference their form by
    // its `form="..."` attribute — that form may not even be on the page if the buy-buttons block
    // happens to be disabled, but the fields themselves still render regardless.
    for (const field of document.querySelectorAll('[name^="properties["]')) {
      if (!(field instanceof HTMLInputElement) && !(field instanceof HTMLSelectElement)) continue;
      if (field instanceof HTMLInputElement && (field.type === 'radio' || field.type === 'checkbox') && !field.checked) {
        continue;
      }

      const match = field.name.match(/^properties\[(.+)\]$/);
      if (match?.[1] && field.value) designUrl.searchParams.set(match[1], field.value);
    }

    // The design page (templates/product.sticker-design.json) renders via layout/sticker-design.liquid,
    // which has no header/footer but does render the bag drawer (the editor opens it there after an
    // add), and snippets/theme-drawer.liquid restores a persisted open drawer on arrival at >= 990px —
    // over the editor's option panel. Leaving the drawer open here would also carry into the outgoing
    // cross-document view transition's snapshot, then abruptly vanish once the new page takes over —
    // so close it first and let the redirect happen once it's done.
    const cartDrawer = /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (
      document.getElementById('cart-drawer')
    );
    if (cartDrawer?.isOpen) await cartDrawer.close();

    window.location.href = designUrl.toString();
  }
}

customElements.define('sticker-upload-redirect', StickerUploadRedirect);
